import React from 'react';
import { FiFileText, FiNavigation, FiGlobe, FiExternalLink, FiCheckCircle, FiFlag } from 'react-icons/fi';
import { Country } from '../../types';

interface CountryCardProps {
  country: Country;
}

const CountryCard: React.FC<CountryCardProps> = ({ country }) => {
  const getStatusBadges = () => {
    const badges = [];
    
    if (country.visa) {
      badges.push({
        label: 'Visa Required',
        icon: <FiFileText className="w-4 h-4" />,
        color: 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/20',
      });
    }
    
    if (country.eta) {
      badges.push({
        label: 'ETA Required',
        icon: <FiNavigation className="w-4 h-4" />,
        color: 'bg-yellow-50 dark:bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/20',
      });
    }
    
    if (country.pre_arrival) {
      badges.push({
        label: 'Pre-Arrival Form',
        icon: <FiGlobe className="w-4 h-4" />,
        color: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-500/20',
      });
    }
    
    if (!badges.length) {
      badges.push({
        label: 'No Requirements',
        icon: <FiCheckCircle className="w-4 h-4" />,
        color: 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 border-green-200 dark:border-green-500/20',
      });
    }
    
    return badges;
  };

  const getRequirementCount = () => {
    let count = 0;
    if (country.visa) count++;
    if (country.eta) count++;
    if (country.pre_arrival) count++;
    if (country.government_portal) count++;
    if (country.mobile_apps && country.mobile_apps.length > 0) count++;
    return count;
  };

  const getCountryCode = (countryName: string) => {
    const countryCodeMap: { [key: string]: string } = {
      'Afghanistan': 'af', 'Albania': 'al', 'Algeria': 'dz', 'Andorra': 'ad', 'Angola': 'ao',
      'Argentina': 'ar', 'Armenia': 'am', 'Australia': 'au', 'Austria': 'at', 'Azerbaijan': 'az',
      'Bahamas': 'bs', 'Bahrain': 'bh', 'Bangladesh': 'bd', 'Barbados': 'bb', 'Belarus': 'by',
      'Belgium': 'be', 'Belize': 'bz', 'Benin': 'bj', 'Bhutan': 'bt', 'Bolivia': 'bo',
      'Bosnia and Herzegovina': 'ba', 'Botswana': 'bw', 'Brazil': 'br', 'Brunei': 'bn', 'Bulgaria': 'bg',
      'Burkina Faso': 'bf', 'Burundi': 'bi', 'Cabo Verde': 'cv', 'Cambodia': 'kh', 'Cameroon': 'cm',
      'Canada': 'ca', 'Central African Republic': 'cf', 'Chad': 'td', 'Chile': 'cl', 'China': 'cn',
      'Colombia': 'co', 'Comoros': 'km', 'Congo, Democratic Republic of the': 'cd', 'Congo, Republic of the': 'cg',
      'Costa Rica': 'cr', 'Cote d\'Ivoire': 'ci', 'Croatia': 'hr', 'Cuba': 'cu', 'Cyprus': 'cy',
      'Czechia': 'cz', 'Denmark': 'dk', 'Djibouti': 'dj', 'Dominica': 'dm', 'Dominican Republic': 'do',
      'Ecuador': 'ec', 'Egypt': 'eg', 'El Salvador': 'sv', 'Equatorial Guinea': 'gq', 'Eritrea': 'er',
      'Estonia': 'ee', 'Eswatini': 'sz', 'Ethiopia': 'et', 'Fiji': 'fj', 'Finland': 'fi',
      'France': 'fr', 'Gabon': 'ga', 'Gambia': 'gm', 'Georgia': 'ge', 'Germany': 'de',
      'Ghana': 'gh', 'Greece': 'gr', 'Grenada': 'gd', 'Guatemala': 'gt', 'Guinea': 'gn',
      'Guinea-Bissau': 'gw', 'Guyana': 'gy', 'Haiti': 'ht', 'Honduras': 'hn', 'Hong Kong': 'hk',
      'Hungary': 'hu', 'Iceland': 'is', 'India': 'in', 'Indonesia': 'id', 'Iran': 'ir',
      'Iraq': 'iq', 'Ireland': 'ie', 'Israel': 'il', 'Italy': 'it', 'Jamaica': 'jm',
      'Japan': 'jp', 'Jordan': 'jo', 'Kazakhstan': 'kz', 'Kenya': 'ke', 'Kiribati': 'ki',
      'Kosovo': 'xk', 'Kuwait': 'kw', 'Kyrgyzstan': 'kg', 'Laos': 'la', 'Latvia': 'lv',
      'Lebanon': 'lb', 'Lesotho': 'ls', 'Liberia': 'lr', 'Libya': 'ly', 'Liechtenstein': 'li',
      'Lithuania': 'lt', 'Luxembourg': 'lu', 'Madagascar': 'mg', 'Malawi': 'mw', 'Malaysia': 'my',
      'Mali': 'ml', 'Malta': 'mt', 'Marshall Islands': 'mh', 'Mauritania': 'mr', 'Mauritius': 'mu',
      'Mexico': 'mx', 'Micronesia': 'fm', 'Moldova': 'md', 'Monaco': 'mc', 'Mongolia': 'mn',
      'Montenegro': 'me', 'Montserrat': 'ms', 'Morocco': 'ma', 'Mozambique': 'mz', 'Myanmar': 'mm',
      'Namibia': 'na', 'Nauru': 'nr', 'Nepal': 'np', 'Netherlands': 'nl', 'New Zealand': 'nz',
      'Nicaragua': 'ni', 'Niger': 'ne', 'Nigeria': 'ng', 'North Korea': 'kp', 'North Macedonia': 'mk',
      'Norway': 'no', 'Oman': 'om', 'Palau': 'pw', 'Palestine': 'ps', 'Panama': 'pa',
      'Papua New Guinea': 'pg', 'Paraguay': 'py', 'Peru': 'pe', 'Philippines': 'ph', 'Poland': 'pl',
      'Portugal': 'pt', 'Qatar': 'qa', 'Romania': 'ro', 'Russia': 'ru', 'Rwanda': 'rw',
      'Saint Kitts and Nevis': 'kn', 'Saint Lucia': 'lc', 'Saint Vincent and the Grenadines': 'vc',
      'Samoa': 'ws', 'San Marino': 'sm', 'Sao Tome and Principe': 'st', 'Saudi Arabia': 'sa', 'Senegal': 'sn',
      'Serbia': 'rs', 'Seychelles': 'sc', 'Sierra Leone': 'sl', 'Singapore': 'sg', 'Slovakia': 'sk',
      'Slovenia': 'si', 'Solomon Islands': 'sb', 'Somalia': 'so', 'South Africa': 'za', 'South Korea': 'kr',
      'South Sudan': 'ss', 'Spain': 'es', 'Sri Lanka': 'lk', 'Sudan': 'sd', 'Suriname': 'sr',
      'Sweden': 'se', 'Switzerland': 'ch', 'Syria': 'sy', 'Taiwan': 'tw', 'Tajikistan': 'tj',
      'Tanzania': 'tz', 'Thailand': 'th', 'Timor-Leste': 'tl', 'Togo': 'tg', 'Tonga': 'to',
      'Trinidad and Tobago': 'tt', 'Tunisia': 'tn', 'Turkey': 'tr', 'Turkmenistan': 'tm', 'Tuvalu': 'tv',
      'Uganda': 'ug', 'Ukraine': 'ua', 'United Arab Emirates': 'ae', 'United Kingdom': 'gb', 'United States': 'us',
      'Uruguay': 'uy', 'Uzbekistan': 'uz', 'Vanuatu': 'vu', 'Vatican City': 'va', 'Venezuela': 've',
      'Vietnam': 'vn', 'Yemen': 'ye', 'Zambia': 'zm', 'Zimbabwe': 'zw', 'Pakistan': 'pk'
    };
    
    return countryCodeMap[countryName] || 'un';
  };

  return (
    <div className="group relative rounded-2xl border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden h-full bg-white dark:bg-slate-800/50 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Flag */}
            <div className="w-12 h-12 rounded-xl overflow-hidden border-2 flex-shrink-0">
              <img 
                src={`https://flagcdn.com/w320/${getCountryCode(country.country)}.png`}
                alt={`${country.country} flag`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = '';
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
            
            <div>
              <h3 className="text-lg font-bold mb-1 text-gray-900 dark:text-white">
                {country.country}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {getRequirementCount()} requirement{getRequirementCount() !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Status Badges */}
        <div className="flex flex-wrap gap-2">
          {getStatusBadges().map((badge, index) => (
            <span
              key={index}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold border ${
                badge.color
              }`}
            >
              {badge.icon}
              {badge.label}
            </span>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="p-6 space-y-3">
        {country.visa && (
          <a
            href={country.visa}
            target="_blank"
            rel="noopener noreferrer"
            className="group/link flex items-center justify-between p-3 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 font-semibold text-sm transition-all duration-300 hover:scale-[1.02] border border-red-200"
          >
            <div className="flex items-center gap-2">
              <FiFileText className="w-4 h-4" />
              <span>Apply for Visa</span>
            </div>
            <FiExternalLink className="w-4 h-4 opacity-0 group-hover/link:opacity-100 transition-opacity" />
          </a>
        )}
        
        {country.eta && (
          <a
            href={country.eta}
            target="_blank"
            rel="noopener noreferrer"
            className="group/link flex items-center justify-between p-3 rounded-xl bg-yellow-50 hover:bg-yellow-100 text-yellow-700 font-semibold text-sm transition-all duration-300 hover:scale-[1.02] border border-yellow-200"
          >
            <div className="flex items-center gap-2">
              <FiNavigation className="w-4 h-4" />
              <span>Apply for ETA</span>
            </div>
            <FiExternalLink className="w-4 h-4 opacity-0 group-hover/link:opacity-100 transition-opacity" />
          </a>
        )}
        
        {country.pre_arrival && (
          <a
            href={country.pre_arrival}
            target="_blank"
            rel="noopener noreferrer"
            className="group/link flex items-center justify-between p-3 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold text-sm transition-all duration-300 hover:scale-[1.02] border border-blue-200"
          >
            <div className="flex items-center gap-2">
              <FiGlobe className="w-4 h-4" />
              <span>Pre-Arrival Form</span>
            </div>
            <FiExternalLink className="w-4 h-4 opacity-0 group-hover/link:opacity-100 transition-opacity" />
          </a>
        )}

        {country.government_portal && (
          <a
            href={country.government_portal}
            target="_blank"
            rel="noopener noreferrer"
            className="group/link flex items-center justify-between p-3 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-700 font-semibold text-sm transition-all duration-300 hover:scale-[1.02] border border-purple-200"
          >
            <div className="flex items-center gap-2">
              <FiGlobe className="w-4 h-4" />
              <span>Government Portal</span>
            </div>
            <FiExternalLink className="w-4 h-4 opacity-0 group-hover/link:opacity-100 transition-opacity" />
          </a>
        )}

        {/* Mobile Apps */}
        {country.mobile_apps && country.mobile_apps.length > 0 && (
          <div className="space-y-2">
            {country.mobile_apps.map((app, index) => (
              <a
                key={index}
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group/link flex items-center justify-between p-3 rounded-xl bg-green-50 hover:bg-green-100 text-green-700 font-semibold text-sm transition-all duration-300 hover:scale-[1.02] border border-green-200"
              >
                <div className="flex items-center gap-2">
                  <FiFlag className="w-4 h-4" />
                  <span>{app.name}</span>
                  <span className="text-xs bg-white px-2 py-0.5 rounded-full border border-green-200">
                    {app.platform === 'google_play' ? 'Google Play' : 'App Store'}
                  </span>
                </div>
                <FiExternalLink className="w-4 h-4 opacity-0 group-hover/link:opacity-100 transition-opacity" />
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      {country.notes && (
        <div className="px-6 pb-6 text-gray-600 dark:text-gray-400">
          <div className="p-3 rounded-xl text-sm bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-gray-700">
            {country.notes}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCard;
